import { defineStore, storeToRefs } from "pinia";
import { computed } from "vue";
import { z } from "zod";

import {
  createConfigStore,
  JsonConfigSource,
  TableauSettingsConfigSource,
} from "@vm/config-store";

import runtimeConfig from "@/config/runtime.config";

import { ComparisonSettingsSchema } from "@/editor/use-settings-editor-store";

type ComparisonConfig = z.infer<typeof ComparisonSettingsSchema>;

const defaultConfig: ComparisonConfig = {
  colors: {
    headerBackground: "#0f3044",
    accordionHeaderBackground: "#e5e7eb",
    subjectRowBackground: "#f9fafb",
    referenceRowBackground: "#f0fdf4",
    typeColors: { Objecten: "#FFA500", Referenties: "#1a4731" },
  },
  layout: {
    minColumnWidth: 200,
    fontSize: 14,
    capitalizeValues: true,
  },
};

const jsonConfigSource = new JsonConfigSource<ComparisonConfig>(defaultConfig);

export const useComparisonConfigStore = defineStore("comparison-config", () => {
  const sources = [
    ...(runtimeConfig.enableTableau
      ? [new TableauSettingsConfigSource<ComparisonConfig>()]
      : []),
    jsonConfigSource,
  ];

  const useBaseConfigStore = createConfigStore(
    "comparison-config-base",
    sources,
    ComparisonSettingsSchema,
  );

  const baseConfigStore = useBaseConfigStore();
  const { configValues } = storeToRefs(baseConfigStore);

  const colors = computed(() => ({
    headerBackground: configValues.value.colors.headerBackground,
    accordionHeaderBackground:
      configValues.value.colors.accordionHeaderBackground,
    subjectRowBackground: configValues.value.colors.subjectRowBackground,
    referenceRowBackground: configValues.value.colors.referenceRowBackground,
    typeColors: configValues.value.colors.typeColors,
  }));

  const minColumnWidth = computed(
    () => configValues.value.layout?.minColumnWidth ?? 200,
  );

  const fontSize = computed(() => configValues.value.layout?.fontSize ?? 14);

  const capitalizeValues = computed(
    () => configValues.value.layout?.capitalizeValues ?? true,
  );

  return {
    colors,
    minColumnWidth,
    fontSize,
    capitalizeValues,
    initialize: baseConfigStore.initialize,
  };
});
